import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import engine from "@/lib/audio";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { quip, moodFor, personaFor } from "@/lib/volt";

const Ctx = createContext(null);
const LS = "pp_audio";
const DEFAULTS = { sound: true, music: true, volume: 80, muted: false };
const norm = (p) => ({ ...DEFAULTS, ...(p || {}), sound: !!(p?.sound ?? true), music: !!(p?.music ?? true), muted: !!p?.muted, volume: Math.min(100, Math.max(0, Number(p?.volume ?? 80))) });
const readLocal = () => { try { return JSON.parse(localStorage.getItem(LS)) || null; } catch { return null; } };

export function GameFeelProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(() => norm(readLocal()));
  const [unlocked, setUnlocked] = useState(false);
  const [bubble, setBubble] = useState(null); // {text, mood, id}
  const winStreak = useRef(0);
  const hideTimer = useRef(null);
  const dirty = useRef(false);
  const tracks = useRef(new Set());

  // adopt server prefs once per login; if the user already toggled locally, push local prefs instead
  useEffect(() => {
    if (!user?.user_id) return;
    if (dirty.current) { api.post("/auth/audio-prefs", prefs).catch(() => {}); return; }
    if (user.audio_prefs) setPrefs(norm(user.audio_prefs));
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    engine.setPrefs(prefs);
    try { localStorage.setItem(LS, JSON.stringify(prefs)); } catch { /* noop */ }
  }, [prefs]);

  // muted until first user gesture (browser autoplay policy)
  useEffect(() => {
    const off = engine.onChange(() => setUnlocked(engine.unlocked));
    const unlock = () => { engine.unlock(); window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
    window.addEventListener("pointerdown", unlock); window.addEventListener("keydown", unlock);
    return () => { off(); window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, []);

  const save = useCallback((next) => {
    dirty.current = true;
    setPrefs(next);
    if (user) api.post("/auth/audio-prefs", next).catch(() => {});
  }, [user]);
  const toggleSound = useCallback(() => { engine.unlock(); save({ ...prefs, sound: !prefs.sound }); if (!prefs.sound) setTimeout(() => engine.sfx("tap"), 30); }, [prefs, save]);
  const toggleMusic = useCallback(() => { engine.unlock(); save({ ...prefs, music: !prefs.music }); }, [prefs, save]);
  const toggleMute = useCallback(() => { engine.unlock(); save({ ...prefs, muted: !prefs.muted }); }, [prefs, save]);
  // slider: live-preview locally while dragging, persist on commit
  const previewVolume = useCallback((v) => { engine.unlock(); dirty.current = true; setPrefs((p) => ({ ...p, volume: v })); }, []);
  const setVolume = useCallback((v) => { save({ ...prefs, volume: v }); setTimeout(() => engine.sfx("tap"), 30); }, [prefs, save]);

  const sfx = useCallback((name) => engine.sfx(name), []);
  // "play" wins over "lobby" regardless of which component mounted first
  const apply = () => engine.setTrack(tracks.current.has("play") ? "play" : tracks.current.has("lobby") ? "lobby" : null);
  const holdTrack = useCallback((name) => { tracks.current.add(name); apply(); return () => { tracks.current.delete(name); apply(); }; }, []);

  const say = useCallback((text, mood = "happy", ms = 4500) => {
    clearTimeout(hideTimer.current);
    setBubble({ text, mood, id: Date.now() });
    hideTimer.current = setTimeout(() => setBubble(null), ms);
  }, []);

  // One call from any game: plays the right sound + Volt reacts in character.
  const react = useCallback(({ outcome, game, big = false, vars = {}, bubble = true }) => {
    if (outcome === "win") winStreak.current += 1; else if (outcome === "loss") winStreak.current = 0;
    const streak = outcome === "win" ? winStreak.current : 0;
    if (outcome === "win") { engine.sfx(streak >= 3 ? "streak" : "fanfare"); setTimeout(() => engine.sfx("coin"), 350); }
    else if (outcome === "loss") engine.sfx("wrong");
    else if (outcome === "daily") { engine.sfx("fanfare"); if (vars.streak > 1) setTimeout(() => engine.sfx("streak"), 500); }
    const text = quip({ outcome, persona: personaFor(game), streak, big, vars });
    if (bubble) say(text, moodFor(outcome));
    return text;
  }, [say]);

  const value = useMemo(() => ({ prefs, unlocked, toggleSound, toggleMusic, toggleMute, previewVolume, setVolume, sfx, holdTrack, say, react, bubble, dismiss: () => setBubble(null) }),
    [prefs, unlocked, toggleSound, toggleMusic, toggleMute, previewVolume, setVolume, sfx, holdTrack, say, react, bubble]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAudio = () => useContext(Ctx);
export const useVolt = () => useContext(Ctx);

// Hold a music loop while a page/arena is mounted ("play" overrides the Layout's "lobby").
export function useTrack(name) {
  const { holdTrack } = useAudio();
  useEffect(() => holdTrack(name), [name, holdTrack]);
}
